import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SiteInformationCreate from "./SiteCreateInformation";
import {
  SiteAddressProps,
  SiteInformationProps,
  SiteProps,
} from "../../common/interfaces/Sites.interface";
import { siteValidationSchemas } from "../../common/validations/SiteValidationsSchemas";
import * as Yup from "yup";
import SiteAddressCreate from "./SiteCreateAddress";
import { ReactComponent as InfoIcon } from "../../common/assats/icons/infoIcon.svg";
import { ReactComponent as MapIcon } from "../../common/assats/icons/addressIcon.svg";
import ButtonCustom from "../common/Button";
import { Metrics, SiteStepsEnum } from "../../common/enums/generalEnum";
import {
  createNewSite,
  updateSite,
  getUserByEmailForSite,
} from "../../common/api/apiCalls";
import { useToast } from "../../common/contexts/useToast";
import { useLocation, useNavigate } from "react-router-dom";
import MainHome from "../MainHome";

const SitesPageCreate = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const siteToEdit = location.state || null;
  const [isEdit, setIsEdit] = useState<boolean>(location.state?.edit || false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [siteInformationData, setSiteInformationData] =
    useState<SiteInformationProps>({
      siteName: "",
      siteNumber: "",
      siteAdminFullName: "",
      siteAdminEmail: "",
      siteAdminPhone: "",
      siteStatus: "Active",
      metrics: "",
      id: "",
    });

  const [siteAddressData, setSiteAddressData] = useState<SiteAddressProps>({
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });

  useEffect(() => {
    if (!siteToEdit || !siteToEdit?.siteName){
      setIsEdit(false);
      return;
    };
    const metrics =
      siteToEdit?.metrics?.length &&
      (siteToEdit?.metrics === Metrics.metricsImperial
        ? t("sitePage.siteCreate.metricsImperial")
        : t("sitePage.siteCreate.metricsMetric"));
    setIsEdit(true);
    setSiteInformationData({
      siteName: siteToEdit.siteName,
      siteNumber: siteToEdit.siteNumber,
      siteAdminFullName: siteToEdit.siteAdminFullName,
      siteAdminEmail: siteToEdit.siteAdminEmail,
      siteAdminPhone: siteToEdit.siteAdminPhone,
      siteStatus: siteToEdit.siteStatus,
      metrics: metrics,
      id: siteToEdit.id,
    });
    setSiteAddressData({
      address: siteToEdit.address,
      city: siteToEdit.city,
      state: siteToEdit.state,
      zipCode: siteToEdit.zipCode,
      country: siteToEdit.country,
    });
  }, [siteToEdit]);

  const navigateToSites = () => {
    navigate("/Home/Sites");
    return;
  };

  const [activeStep, setActiveStep] = useState<SiteStepsEnum>(
    SiteStepsEnum.SITE_INFORMATION
  );

  const stepConfig = {
    [SiteStepsEnum.SITE_INFORMATION]: {
      schema: siteValidationSchemas[SiteStepsEnum.SITE_INFORMATION],
      data: siteInformationData,
    },
    [SiteStepsEnum.SITE_ADDRESS]: {
      schema: siteValidationSchemas[SiteStepsEnum.SITE_ADDRESS],
      data: siteAddressData,
    },
  };

  const handleInformationChange = (name: string, value: any) => {
    setSiteInformationData((prevData: SiteInformationProps) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddressChange = (name: string, value: any) => {
    setSiteAddressData((prevData: SiteAddressProps) => ({
      ...prevData,
      [name]: value,
    }));
  };


  const handleNext = async () => {
    try {
      const { schema, data } = stepConfig[activeStep];
      await schema.validate(data, { abortEarly: false });
      setErrors({});
      if (isEdit) {
        const user = await getUserByEmailForSite(
          siteInformationData.siteAdminEmail
        );
        if (!user) {
          showToast(
            "error",
            "Error",
            t("sitePage.siteCreate.errors.userNotFoundByEmail")
          );
          return;
        }
      }
      setActiveStep(SiteStepsEnum.SITE_ADDRESS);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = error.message;
          }
        });
        setErrors(validationErrors);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const siteAddressSchema =
        siteValidationSchemas[SiteStepsEnum.SITE_ADDRESS];
      const siteInformationSchema =
        siteValidationSchemas[SiteStepsEnum.SITE_INFORMATION];
      await siteAddressSchema.validate(siteAddressData, { abortEarly: false });
      await siteInformationSchema.validate(siteInformationData, {
        abortEarly: false,
      });

      let metrics = "";
      if (
        siteInformationData.metrics
          ?.toLowerCase()
          .includes(Metrics.metricsImperial)
      ) {
        metrics = Metrics.metricsImperial;
      } else if (
        siteInformationData.metrics
          ?.toLowerCase()
          .includes(Metrics.metricsMetric)
      ) {
        metrics = Metrics.metricsMetric;
      }

      const dataToSubmit:SiteProps = {
        ...siteInformationData,
        ...siteAddressData,
        metrics,
      };
      let res = null;
      if (isEdit) {
        res = await updateSite(dataToSubmit);
      } else {
        const { id, ...dataToSubmitWithoutId } = dataToSubmit;
        res = await createNewSite(dataToSubmitWithoutId);
      }
      if (res.success) {
        showToast(
          "success",
          "Success",
          isEdit
            ? t("sitePage.siteCreate.siteUpdateSuccess")
            : t("sitePage.siteCreate.siteCreateSuccess")
        );
        navigateToSites();
      } else {
        showToast("error", "Error", t(res.data.errorMessage));
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = error.message;
          }
        });
        setErrors(validationErrors);
      }
    }
  };

  return (
    <MainHome>
      <div className="page-container w-100 h-100 p-5 border rounded background-primary-white">
        <div className="pr-5 pl-5 h-100 d-flex flex-column">
          <div className="mb-4">
            <label className="fw-bold heading-h3">
              {isEdit
                ? t("sitePage.siteCreate.editSite")
                : t("sitePage.siteCreate.addNewSite")}
            </label>
            <hr className="my-4" />
            <div className="ms-2 d-flex align-items-end">
              {activeStep === SiteStepsEnum.SITE_INFORMATION ? (
                <>
                  <InfoIcon />
                  <label className="ms-2 heading-h4">
                    {t("sitePage.siteCreate.siteInformation")}
                  </label>
                </>
              ) : (
                <>
                  <MapIcon />
                  <label className="ms-2 heading-h4">{t("sitePage.siteCreate.address")}</label>
                </>
              )}
            </div>
          </div>

          <div className="flex-grow-1">
            {activeStep === SiteStepsEnum.SITE_INFORMATION ? (
              <SiteInformationCreate
                values={siteInformationData}
                handleInputChange={handleInformationChange}
                errors={errors}
              />
            ) : (
              <SiteAddressCreate
                values={siteAddressData}
                handleInputChange={handleAddressChange}
                errors={errors}
              />
            )}
          </div>

          <hr className="form-footer-divider" />
          <div className="form-footer">
            {activeStep === SiteStepsEnum.SITE_INFORMATION ? (
              <>
                <ButtonCustom
                  type="button"
                  onClick={navigateToSites}
                  className="btn-secondary btn-lg"
                  context={t("general.buttons.cancel")}
                />
                <ButtonCustom
                  type="button"
                  onClick={handleNext}
                  className="btn-primary btn-lg"
                  context={t("general.buttons.next")}
                />
              </>
            ) : (
              <>
                <ButtonCustom
                  type="button"
                  onClick={() => setActiveStep(SiteStepsEnum.SITE_INFORMATION)}
                  className="btn-secondary btn-lg"
                  context={t("general.buttons.back")}
                />
                <ButtonCustom
                  type="button"
                  onClick={handleSubmit}
                  className="btn-primary btn-lg"
                  context={
                    isEdit
                      ? t("general.buttons.save")
                      : t("sitePage.siteCreate.addSiteButton")
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>
    </MainHome>
  );
};

export default SitesPageCreate;
